import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { clearAllCache } from "../utils/cacheManager";

const EventContext = createContext(null);
import { BACKEND_URL as API } from '../utils/apiBase';

// Module-level variable for axios interceptor
let _currentEventId = null;
// Promise-gate: any request that arrives before the event_id is loaded
// will await this promise. Prevents race conditions where the very first
// /battle/current call goes out with no event_id and returns the wrong battle.
let _eventIdReady;
let _resolveEventIdReady;
const _initEventIdGate = () => {
  _eventIdReady = new Promise((res) => { _resolveEventIdReady = res; });
};
_initEventIdGate();

// Axios interceptor: automatically add event_id to all API requests
axios.interceptors.request.use(async (config) => {
  if (config.url?.includes('/api/')) {
    // Skip auth and admin routes, plus the new public events resolver.
    if (
      config.url.includes('/api/auth/') ||
      config.url.includes('/api/admin/') ||
      config.url.includes('/api/events/by-slug/')
    ) return config;
    // Wait for the event_id to be resolved before letting event-scoped
    // requests fly. This avoids a 1-2s window at app boot where the
    // interceptor would otherwise let the request through without event_id.
    if (!_currentEventId) {
      try {
        await Promise.race([_eventIdReady, new Promise(r => setTimeout(r, 5000))]);
      } catch (_e) { /* fall through with no event_id */ }
    }
    if (_currentEventId) {
      config.params = { ...(config.params || {}), event_id: _currentEventId };
    }
  }
  return config;
});

export const useEvent = () => useContext(EventContext);

// --- Retry helpers ------------------------------------------------------
// Statuses that are almost always transient at the edge / network layer.
// A single failure on any of these must NEVER show the user a permanent
// "Access denied" screen — we retry silently first.
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

// Backoff plan for the initial event resolution: 4 tries over ~15 seconds.
// Kept intentionally small and bounded so we do not hammer a struggling
// backend, but long enough to ride out a venue Wi-Fi micro-outage.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

// Extract the diagnostics bundle asked for in the incident brief. We log
// this instead of the raw error so we get actionable data without leaking
// tokens/cookies/bodies.
const describeFailure = (err, url) => {
  const res = err?.response;
  const headers = res?.headers || {};
  return {
    request_url: url,
    http_status: res?.status ?? null,
    content_type: headers['content-type'] || headers['Content-Type'] || null,
    cf_ray: headers['cf-ray'] || null,
    cf_mitigated: headers['cf-mitigated'] || null,
    network_error: !res, // no response at all => DNS/TCP/TLS/offline
    message: err?.message || String(err),
  };
};

const isTransient = (err) => {
  if (!err?.response) return true; // network error, offline, DNS, etc.
  return TRANSIENT_STATUSES.has(err.response.status);
};

const EventLayout = ({ children }) => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | reconnecting | not_found | error | ok
  const [attempt, setAttempt] = useState(0);
  const cancelledRef = useRef(false);
  const retryTimerRef = useRef(null);

  const loadEvent = useCallback(async () => {
    // Public, read-only endpoint. Kept OUTSIDE /api/admin so it cannot be
    // accidentally gated behind admin auth later. Returns just event_id,
    // slug, name, status.
    const url = `${API}/api/events/by-slug/${slug}`;

    for (let i = 0; i < RETRY_DELAYS_MS.length + 1; i++) {
      if (cancelledRef.current) return;
      try {
        const res = await axios.get(url, { timeout: 10000 });
        if (cancelledRef.current) return;
        setEvent(res.data);
        _currentEventId = res.data.event_id;
        if (_resolveEventIdReady) _resolveEventIdReady();
        setStatus("ok");
        setAttempt(0);
        return;
      } catch (err) {
        const info = describeFailure(err, url);
        console.warn(`[EventLayout] load attempt ${i + 1} failed`, info);

        if (cancelledRef.current) return;

        // 404 is definitive: the slug really doesn't exist. Show a clear
        // message immediately, no retry needed.
        if (err?.response?.status === 404) {
          setStatus("not_found");
          if (_resolveEventIdReady) _resolveEventIdReady();
          return;
        }

        const canRetry = i < RETRY_DELAYS_MS.length && isTransient(err);
        if (canRetry) {
          setStatus("reconnecting");
          setAttempt(i + 1);
          await new Promise((resolve) => {
            retryTimerRef.current = setTimeout(resolve, RETRY_DELAYS_MS[i]);
          });
          continue;
        }

        // Out of retries, or a hard non-transient error (e.g. 401/403 that
        // stayed 401/403 across attempts). Do NOT nuke the app with a
        // permanent black screen — show a soft error card with a manual
        // "Retry now" button.
        setStatus("error");
        if (_resolveEventIdReady) _resolveEventIdReady();
        return;
      }
    }
  }, [slug]);

  useEffect(() => {
    cancelledRef.current = false;
    setStatus("loading");
    setAttempt(0);
    loadEvent();

    return () => {
      cancelledRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      _currentEventId = null;
      _initEventIdGate();
      clearAllCache();
    };
  }, [slug, loadEvent]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="event-loading">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (status === "reconnecting") {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 p-4" data-testid="event-reconnecting">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        <div className="text-gray-300 text-sm tracking-wide">
          Reconnexion en cours… (tentative {attempt}/{RETRY_DELAYS_MS.length})
        </div>
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="event-not-found">
        <Card className="p-8 text-center max-w-md" style={{ background: 'rgba(20,20,30,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-2xl font-bold text-red-400 mb-2">Event not found</h2>
          <p className="text-gray-500">The event you&apos;re looking for doesn&apos;t exist or is inactive.</p>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="event-error">
        <Card className="p-8 text-center max-w-md" style={{ background: 'rgba(20,20,30,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 className="text-2xl font-bold text-yellow-400 mb-2">Connexion instable</h2>
          <p className="text-gray-400 mb-6">
            Impossible de charger l&apos;événement pour le moment. Le réseau ou le service est temporairement indisponible.
          </p>
          <Button
            data-testid="event-error-retry-btn"
            onClick={() => {
              setStatus("loading");
              setAttempt(0);
              loadEvent();
            }}
          >
            Réessayer maintenant
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <EventContext.Provider value={event}>
      {children}
    </EventContext.Provider>
  );
};

export default EventLayout;
